import { createApi } from '@reduxjs/toolkit/dist/query/react';
import cookies from 'js-cookie';

import { cookieNames } from '@monorepo/common';

import { baseQueryWithReAuth } from '../../utils/baseQueryWithReAuth';
import endpoints from '../../api/endpoints';
import {
  JobListingTypesResponse,
  RespSalaryTypes,
  IRequestStepsData,
  ResponseItem,
  IRespAllSteps,
  IPaymentResponse,
  IResponseBrewboundList,
  IPaymentIntentRequest,
  IPaymentIntentResponse,
  ISearchSupplierRequest,
  IBenefitQuoteRequest,
  IBenefitQuoteResponse,
  IBenefitCheckoutRequest,
  IBenefitCheckoutResponse,
  IRespFreeSteps
} from './responseInterfaces';

export const postAJobApi = createApi({
  reducerPath: 'servicePostAJob',
  tagTypes: ['IndustrySectors'],
  baseQuery: baseQueryWithReAuth,
  endpoints: (build) => ({
    // step 1
    getJobListingTypes: build.query<JobListingTypesResponse, { id: string; code?: string }>({
      query: ({ id, code }: { id: string; code?: string }) => ({
        url: endpoints.jobs.jobListingTypes(id),
        params: code ? { code } : undefined,
      }),
    }),
    // step 2

    getIndustry: build.query<ResponseItem[], string>({
      query: (id: string) => ({
        url: endpoints.jobs.industrySectors(id),
      }),
      providesTags: ['IndustrySectors']
    }),

    getBrewboundCompanyList: build.query<IResponseBrewboundList[], string>({
      query: (text: string) => ({
        url: endpoints.jobs.brewboundCompanyList(text)
      }),
    }),

    getEmploymentTypes: build.query<ResponseItem[], null>({
      query: () => ({
        url: endpoints.jobs.employmentTypes,
      }),
    }),

    getSalaryUnits: build.query<ResponseItem[], null>({
      query: () => ({
        url: endpoints.jobs.salaryUnits,
      }),
    }),

    getSalaryTypes: build.query<RespSalaryTypes[], null>({
      query: () => ({
        url: endpoints.jobs.salaryTypes,
      }),
    }),

    postLogo: build.mutation<{ url: string }, any>({
      query: (body:any) => ({
        url: endpoints.jobs.jobLogo,
        method: 'POST',
        body
      }),
    }),

    jobLogoFromSupplier: build.mutation<{ url: string | null }, { supplierId: number }>({
      query: (body) => ({
        url: endpoints.jobs.jobLogoFromSupplier,
        method: 'POST',
        body
      }),
    }),

    searchSupplier: build.mutation<ResponseItem[], ISearchSupplierRequest>({
      query: (body) => ({
        url: endpoints.jobs.searchSupplier,
        method: 'POST',
        body
      })
    }),

    // get temp data
    getJobTempData: build.query<IRespAllSteps, string>({
      query: (guid: string) => ({
        url: endpoints.jobs.getJobTempData(guid)
      }),
    }),

    // save all data from steps
    saveStepsData: build.mutation<{ guid: string }, IRequestStepsData>({
      query: (body:IRequestStepsData) => {
        if (cookies.get(cookieNames.tempJob)) {
          return {
            url: endpoints.jobs.saveJobData,
            method: 'POST',
            params: { guid: cookies.get(cookieNames.tempJob) },
            body
          };
        }
        return {
          url: endpoints.jobs.saveJobData,
          method: 'POST',
          body
        };
      }
    }),

    // Clear the post-a-job temp bucket. The guid is a parameter, not a cookie read
    // inside query(): callers remove the cookie as part of finishing up, so by the
    // time RTK Query built the URL it was already gone and the request went out as
    // /Jobs/ClearTempData/undefined (D-24094 §20). Mirrors the prepaid endpoint.
    clearData: build.query<any, { guid: string }>({
      query: ({ guid }) => ({
        url: endpoints.jobs.clearPostJobTempData(guid),
      }),
    }),
    getPaymentIntent: build.mutation<IPaymentIntentResponse, IPaymentIntentRequest>({
      query: (body) => ({
        url: endpoints.jobs.getPaymentIntent,
        method: 'post',
        body
      })
    }),
    benefitQuote: build.mutation<IBenefitQuoteResponse, IBenefitQuoteRequest>({
      query: (body) => ({
        url: endpoints.jobs.benefitQuote,
        method: 'post',
        body
      }),
      // BenefitSelector renders the quote error inline (with a "Remove code" way out);
      // a competing global "Something went wrong" toast would only confuse.
      extraOptions: { suppressErrorNotification: true }
    }),
    benefitCheckout: build.mutation<IBenefitCheckoutResponse, IBenefitCheckoutRequest>({
      query: (body) => ({
        url: endpoints.jobs.benefitCheckout,
        method: 'post',
        body
      })
    }),
    releaseBenefitCheckout: build.mutation<void, { claimCheckoutGuid: string; contactEmail?: string }>({
      query: (body) => ({
        url: endpoints.jobs.releaseBenefitCheckout,
        method: 'post',
        body
      })
    }),
    //  FINAL REQUEST
    paymentRequest: build.mutation<IPaymentResponse, { id: string, validateOnly: boolean, body: IRespAllSteps }>({
      query: ({ id, validateOnly, body }) => ({
        url: endpoints.jobs.postJobListing(id, validateOnly),
        method: 'post',
        body
      }),
    }),
    freePaymentRequest: build.mutation<IPaymentResponse, { id: string, validateOnly: boolean, body: IRespFreeSteps }>({
      query: ({ id, validateOnly, body }) => ({
        url: endpoints.jobs.postJobListingWithBenefit(id, validateOnly),
        method: 'post',
        body
      }),
    }),
  }),
});
export const {
  useGetIndustryQuery,
  useGetEmploymentTypesQuery,
  useGetSalaryUnitsQuery,
  useGetJobListingTypesQuery,
  useGetSalaryTypesQuery,
  useGetJobTempDataQuery,
  useSearchSupplierMutation,

  useLazyGetBrewboundCompanyListQuery,

  usePostLogoMutation,
  useJobLogoFromSupplierMutation,
  useSaveStepsDataMutation,

  useClearDataQuery,

  usePaymentRequestMutation,
  useFreePaymentRequestMutation,
  useGetPaymentIntentMutation,
  useBenefitQuoteMutation,
  useBenefitCheckoutMutation,
  useReleaseBenefitCheckoutMutation
} = postAJobApi;
